import { useState, useEffect, useCallback, useRef } from 'react'
import { useNodesState, useEdgesState, Node, Edge } from 'reactflow'
import { useAuthStore } from '../stores/auth'
import { readSSEStream } from '../utils/sse-reader'
import { buildMindMapLayout } from '../components/promptcraft/MindMapView'
import type { ExpandedResult, StructuredPrompt, SubjectStructure, HistoryRecord } from '../types/promptcraft'

const API_BASE = 'https://cloudflare-mutimodal-backend.steven-mei0814.workers.dev'

export function usePromptCraftState() {
  const { token } = useAuthStore()

  const [mode, setMode] = useState<'generate' | 'decompose' | 'vision'>('generate')
  const [seedPrompt, setSeedPrompt] = useState('')
  const [decomposePrompt, setDecomposePrompt] = useState('')
  const [stylePreference, setStylePreference] = useState('')
  const [aspectRatio, setAspectRatio] = useState('16:9')
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)

  const [expandedResult, setExpandedResult] = useState<ExpandedResult | null>(null)
  const [, setRecordId] = useState('')
  const [copied, setCopied] = useState(false)

  const [previewImage, setPreviewImage] = useState<{ url: string; loading: boolean } | null>(null)
  const [previewModal, setPreviewModal] = useState<{ url: string; prompt: string } | null>(null)

  const [showHistory, setShowHistory] = useState(false)
  const [historyRecords, setHistoryRecords] = useState<HistoryRecord[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)

  // Vision mode state
  const [visionFile, setVisionFile] = useState<File | null>(null)
  const [visionPreviewUrl, setVisionPreviewUrl] = useState<string | null>(null)
  const [visionUnderstanding, setVisionUnderstanding] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(
    new Set(['subject', 'style', 'lighting', 'camera', 'environment', 'mood_material'])
  )
  const [draggedNodePositions, setDraggedNodePositions] = useState<Record<string, { x: number; y: number }>>({})

  const [editingNodeId, setEditingNodeId] = useState<string | null>(null)
  const [editingValue, setEditingValue] = useState('')
  const [isRegenerating, setIsRegenerating] = useState(false)
  const savedPositions = useRef<Record<string, { x: number; y: number }>>({})

  // ESC key handler
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && previewModal) setPreviewModal(null)
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [previewModal])

  // Update mind map helper
  const updateMindMap = useCallback((result: ExpandedResult) => {
    const { nodes: newNodes, edges: newEdges } = buildMindMapLayout({
      result,
      expandedNodes,
      editingNodeId,
      savedPositions: savedPositions.current,
      handleStartEdit,
    })
    setNodes(newNodes)
    setEdges(newEdges)
  }, [expandedNodes, editingNodeId, setNodes, setEdges])

  // Load history
  const loadHistory = useCallback(async () => {
    if (!token) return
    setHistoryLoading(true)
    try {
      const response = await fetch(`${API_BASE}/api/promptcraft/history?page=1&limit=10`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (response.ok) {
        const result = await response.json()
        if (result.success) setHistoryRecords(result.data.records || [])
      }
    } catch (error) {
      console.error('Failed to load history:', error)
    } finally {
      setHistoryLoading(false)
    }
  }, [token])

  // Load latest record on mount
  useEffect(() => {
    const loadLatestRecord = async () => {
      if (!token) return
      try {
        const response = await fetch(`${API_BASE}/api/promptcraft/history?page=1&limit=1`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!response.ok) return
        const result = await response.json()
        if (!result.success || !result.data?.records?.length) return
        restoreFromRecord(result.data.records[0])
      } catch (error) {
        console.error('Failed to load latest record:', error)
      }
    }
    loadLatestRecord()
  }, [token])

  // Re-render mind map when expanded nodes change
  useEffect(() => {
    if (expandedResult) {
      updateMindMap(expandedResult)
    }
  }, [expandedNodes])

  // Update isEditing flag without rebuilding nodes
  useEffect(() => {
    setNodes(current => current.map(node =>
      node.type === 'editableLeaf'
        ? { ...node, data: { ...node.data, isEditing: node.id === editingNodeId } }
        : node
    ))
  }, [editingNodeId])

  const restoreFromRecord = (record: HistoryRecord) => {
    setSeedPrompt(record.seed_prompt || '')
    setStylePreference(record.style_preference || '')
    setAspectRatio(record.aspect_ratio || '16:9')
    setRecordId(record.id)

    if (record.expanded_results) {
      try {
        const data = typeof record.expanded_results === 'string'
          ? JSON.parse(record.expanded_results)
          : record.expanded_results
        if (data.full_prompt && data.structured) {
          setExpandedResult(data)
          // Inline updateMindMap since the callback may not have latest expandedNodes yet
          const { nodes: newNodes, edges: newEdges } = buildMindMapLayout({
            result: data,
            expandedNodes,
            editingNodeId,
            savedPositions: savedPositions.current,
            handleStartEdit,
          })
          setNodes(newNodes)
          setEdges(newEdges)
        }
      } catch (e) {
        console.error('Failed to parse expanded_results:', e)
      }
    }
    setShowHistory(false)
  }

  const toggleNodeExpand = useCallback((nodeId: string) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev)
      if (newSet.has(nodeId)) newSet.delete(nodeId)
      else newSet.add(nodeId)
      return newSet
    })
  }, [])

  const handleStartEdit = useCallback((nodeId: string, fullText: string) => {
    setEditingNodeId(nodeId)
    setEditingValue(fullText)
  }, [])

  const handleCancelEdit = useCallback(() => {
    setEditingNodeId(null)
    setEditingValue('')
  }, [])

  const handleSaveEdit = useCallback(() => {
    if (!editingNodeId || !expandedResult) return

    const newStructured = JSON.parse(JSON.stringify(expandedResult.structured)) as StructuredPrompt

    if (editingNodeId.startsWith('subject-value-')) {
      const key = editingNodeId.replace('subject-value-', '') as keyof SubjectStructure
      newStructured.subject[key] = editingValue
    } else if (editingNodeId.endsWith('-content')) {
      const parent = editingNodeId.replace('-content', '') as keyof Omit<StructuredPrompt, 'subject'>
      ;(newStructured as any)[parent] = editingValue
    }

    const updatedResult = { ...expandedResult, structured: newStructured }
    setExpandedResult(updatedResult)
    setEditingNodeId(null)
    setEditingValue('')
    const { nodes: newNodes, edges: newEdges } = buildMindMapLayout({
      result: updatedResult,
      expandedNodes,
      editingNodeId: null,
      savedPositions: savedPositions.current,
      handleStartEdit,
    })
    setNodes(newNodes)
    setEdges(newEdges)
  }, [editingNodeId, editingValue, expandedResult, expandedNodes, setNodes, setEdges])

  const handleRegenerate = useCallback(async () => {
    if (!expandedResult) return
    setIsRegenerating(true)
    try {
      const response = await fetch(`${API_BASE}/api/promptcraft/regenerate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ structured: expandedResult.structured }),
      })
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
      const result = await response.json()
      if (result.success && result.full_prompt) {
        setExpandedResult(prev => prev ? { ...prev, full_prompt: result.full_prompt } : prev)
      }
    } catch (error) {
      console.error('Regenerate error:', error)
      alert('重新生成提示词失败: ' + (error as Error).message)
    } finally {
      setIsRegenerating(false)
    }
  }, [expandedResult, token])

  // Node interaction handlers
  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    const branchIds = ['subject', 'style', 'lighting', 'camera', 'environment', 'mood_material']
    if (branchIds.includes(node.id)) {
      toggleNodeExpand(node.id)
    }
  }, [toggleNodeExpand])

  const findAllChildren = useCallback((nodeId: string, currentEdges: Edge[]): string[] => {
    const children: string[] = []
    const directChildren = currentEdges.filter(edge => edge.source === nodeId).map(edge => edge.target)
    for (const childId of directChildren) {
      children.push(childId)
      children.push(...findAllChildren(childId, currentEdges))
    }
    return children
  }, [])

  const onNodeDragStart = useCallback((_event: React.MouseEvent, node: Node) => {
    setDraggedNodePositions(prev => ({
      ...prev,
      [node.id]: { x: node.position.x, y: node.position.y }
    }))
  }, [])

  const onNodeDrag = useCallback((_event: React.MouseEvent, node: Node) => {
    const originalPos = draggedNodePositions[node.id]
    if (!originalPos) return

    const deltaX = node.position.x - originalPos.x
    const deltaY = node.position.y - originalPos.y
    if (deltaX === 0 && deltaY === 0) return

    const childrenIds = findAllChildren(node.id, edges)
    if (childrenIds.length === 0) return

    setNodes(currentNodes =>
      currentNodes.map(n => {
        if (childrenIds.includes(n.id)) {
          return { ...n, position: { x: n.position.x + deltaX, y: n.position.y + deltaY } }
        }
        return n
      })
    )

    setDraggedNodePositions(prev => ({
      ...prev,
      [node.id]: { x: node.position.x, y: node.position.y }
    }))
  }, [draggedNodePositions, edges, findAllChildren, setNodes])

  const onNodeDragStop = useCallback((_event: React.MouseEvent, _node: Node) => {
    setNodes(current => {
      current.forEach(n => {
        savedPositions.current[n.id] = { x: n.position.x, y: n.position.y }
      })
      return current
    })
  }, [setNodes])

  // SSE stream handler for expand/decompose
  const runSSEStream = useCallback(async (url: string, body: object) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    })
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

    await readSSEStream<ExpandedResult>({
      response,
      isValidPartial: (p): p is ExpandedResult => !!(p?.full_prompt && p?.structured),
      onPartialResult: (_accumulated, parsed) => {
        setExpandedResult(parsed)
        const { nodes: newNodes, edges: newEdges } = buildMindMapLayout({
          result: parsed,
          expandedNodes,
          editingNodeId,
          savedPositions: savedPositions.current,
          handleStartEdit,
        })
        setNodes(newNodes)
        setEdges(newEdges)
      },
      onComplete: (data) => {
        setRecordId(data.record_id || '')
        setIsGenerating(false)
      },
      onError: (error) => { throw error },
    })
  }, [token, expandedNodes, editingNodeId, setNodes, setEdges])

  const handleGenerate = async () => {
    if (!seedPrompt.trim()) { alert('请输入创意种子'); return }

    setIsGenerating(true)
    setExpandedResult(null)
    setPreviewImage(null)
    setRecordId('')
    setNodes([])
    setEdges([])

    try {
      await runSSEStream(`${API_BASE}/api/promptcraft/expand`, {
        seed_prompt: seedPrompt,
        style_preference: stylePreference || null,
        aspect_ratio: aspectRatio,
      })
    } catch (error) {
      console.error('Generate error:', error)
      alert('生成失败: ' + (error as Error).message)
    } finally {
      setIsGenerating(false)
    }
  }

  const handleDecompose = async () => {
    if (!decomposePrompt.trim()) { alert('请输入完整提示词'); return }

    setIsGenerating(true)
    setExpandedResult(null)
    setPreviewImage(null)
    setRecordId('')
    setNodes([])
    setEdges([])
    savedPositions.current = {}

    try {
      await runSSEStream(`${API_BASE}/api/promptcraft/decompose`, {
        prompt: decomposePrompt,
      })
    } catch (error) {
      console.error('Decompose error:', error)
      alert('拆解失败: ' + (error as Error).message)
    } finally {
      setIsGenerating(false)
    }
  }

  // Vision mode
  const handleVisionFileSelect = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) { alert('请选择图片文件'); return }
    if (file.size > 10 * 1024 * 1024) { alert('图片文件不能超过 10MB'); return }
    setVisionFile(file)
    const reader = new FileReader()
    reader.onload = (e) => setVisionPreviewUrl(e.target?.result as string)
    reader.readAsDataURL(file)
  }, [])

  const handleVisionAnalyze = useCallback(async () => {
    if (!visionFile || !token) return

    setIsGenerating(true)
    setVisionUnderstanding(true)
    setExpandedResult(null)
    setPreviewImage(null)
    setRecordId('')
    setNodes([])
    setEdges([])
    savedPositions.current = {}

    try {
      // Step 1: Image understanding
      const { apiClient } = await import('../utils/api')
      const understandResult = await apiClient.understandImage(visionFile)

      if (!understandResult.success || !understandResult.prompt) {
        throw new Error(understandResult.error || '图片理解失败')
      }

      setVisionUnderstanding(false)

      // Step 2: Decompose via SSE
      await runSSEStream(`${API_BASE}/api/promptcraft/decompose`, {
        prompt: understandResult.prompt,
      })
    } catch (error) {
      console.error('Vision analyze error:', error)
      alert('图片理解失败: ' + (error as Error).message)
    } finally {
      setIsGenerating(false)
      setVisionUnderstanding(false)
    }
  }, [visionFile, token, runSSEStream])

  const handleGeneratePreview = async () => {
    if (!expandedResult) return
    if (previewImage?.url) { setPreviewModal({ url: previewImage.url, prompt: expandedResult.full_prompt }); return }

    setPreviewImage({ url: '', loading: true })
    try {
      const response = await fetch(`${API_BASE}/api/promptcraft/generate-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ prompt: expandedResult.full_prompt }),
      })
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
      const result = await response.json()
      if (result.success && result.url) {
        setPreviewImage({ url: result.url, loading: false })
        setPreviewModal({ url: result.url, prompt: expandedResult.full_prompt })
      } else throw new Error(result.error || '生成失败')
    } catch (error) {
      console.error('Generate preview error:', error)
      alert('生成预览图失败: ' + (error as Error).message)
      setPreviewImage(null)
    }
  }

  const handleCopy = () => {
    if (!expandedResult) return
    navigator.clipboard.writeText(expandedResult.full_prompt)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleNewTask = () => {
    setMode('generate')
    setSeedPrompt('')
    setDecomposePrompt('')
    setStylePreference('')
    setAspectRatio('16:9')
    setExpandedResult(null)
    setPreviewImage(null)
    setRecordId('')
    setNodes([])
    setEdges([])
    setEditingNodeId(null)
    setEditingValue('')
    setVisionFile(null)
    setVisionPreviewUrl(null)
    savedPositions.current = {}
  }

  const handleClearVision = () => {
    setVisionFile(null)
    setVisionPreviewUrl(null)
  }

  const handleOpenPreview = (url: string) => {
    setPreviewModal({ url, prompt: '上传图片' })
  }

  return {
    // State
    mode, setMode,
    seedPrompt, setSeedPrompt,
    decomposePrompt, setDecomposePrompt,
    stylePreference, setStylePreference,
    aspectRatio, setAspectRatio,
    showAdvanced, setShowAdvanced,
    isGenerating,
    expandedResult,
    copied,
    previewImage,
    previewModal, setPreviewModal,
    showHistory, setShowHistory,
    historyRecords,
    historyLoading,
    visionFile,
    visionPreviewUrl,
    visionUnderstanding,
    isDragging, setIsDragging,
    fileInputRef,
    nodes, edges,
    onNodesChange, onEdgesChange,
    editingNodeId,
    editingValue, setEditingValue,
    isRegenerating,
    // Handlers
    handleGenerate,
    handleDecompose,
    handleRegenerate,
    handleSaveEdit,
    handleCancelEdit,
    handleGeneratePreview,
    handleCopy,
    handleNewTask,
    handleVisionFileSelect,
    handleVisionAnalyze,
    handleClearVision,
    handleOpenPreview,
    loadHistory,
    restoreFromRecord,
    onNodeClick,
    onNodeDragStart,
    onNodeDrag,
    onNodeDragStop,
  }
}
